import { useEffect, useState, useCallback } from 'react';
import { ConfirmDialog, Pagination } from '../../components';
import { staffSponsorsService, mediaUrl, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { SponsorKind, StaffSponsor } from '../../lib/types';
import { Search, Plus, Pencil, Trash2, Award, HeartHandshake, Instagram, Loader2, ArrowUp, ArrowDown } from 'lucide-react';

type FormData = {
  name: string;
  website_url: string;
  instagram: string;
  kind: SponsorKind;
  display_order: string;
  is_active: boolean;
};

const EMPTY_FORM: FormData = { name: '', website_url: '', instagram: '', kind: 'sponsor', display_order: '0', is_active: true };

const KIND_LABEL: Record<SponsorKind, string> = {
  partner: 'Partener',
  sponsor: 'Sponsor',
};

export default function ManageSponsors() {
  const [sponsors, setSponsors] = useState<StaffSponsor[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StaffSponsor | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: { page?: number; search?: string } = { page };
      if (searchQuery) params.search = searchQuery;
      const data = await staffSponsorsService.list(params);
      setSponsors(data.results);
      setTotalCount(data.count);
    } catch {
      setError('Nu s-au putut încărca partenerii și sponsorii.');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [searchQuery]);

  function startAdd() {
    setIsAdding(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
  }

  function startEdit(s: StaffSponsor) {
    setEditingId(s.id);
    setIsAdding(false);
    setForm({
      name: s.name,
      website_url: s.website_url,
      instagram: s.instagram || '',
      kind: s.kind,
      display_order: String(s.display_order),
      is_active: s.is_active,
    });
    setLogoFile(null);
    setLogoPreview(mediaUrl(s.logo) || null);
    setError('');
  }

  function cancelForm() {
    setIsAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setError('');
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Numele este obligatoriu.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (logoFile) {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('website_url', form.website_url);
        fd.append('instagram', form.instagram);
        fd.append('kind', form.kind);
        fd.append('display_order', form.display_order || '0');
        fd.append('is_active', String(form.is_active));
        fd.append('logo', logoFile);
        if (isAdding) {
          await staffSponsorsService.create(fd);
        } else {
          await staffSponsorsService.updateWithFile(editingId!, fd);
        }
      } else if (isAdding) {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('website_url', form.website_url);
        fd.append('instagram', form.instagram);
        fd.append('kind', form.kind);
        fd.append('display_order', form.display_order || '0');
        fd.append('is_active', String(form.is_active));
        await staffSponsorsService.create(fd);
      } else {
        await staffSponsorsService.update(editingId!, {
          name: form.name,
          website_url: form.website_url,
          instagram: form.instagram,
          kind: form.kind,
          display_order: parseInt(form.display_order || '0', 10),
          is_active: form.is_active,
        });
      }
      cancelForm();
      await load();
    } catch {
      setError('Eroare la salvare.');
    } finally {
      setSaving(false);
    }
  }

  async function nudgeOrder(s: StaffSponsor, delta: number) {
    try {
      await staffSponsorsService.update(s.id, { display_order: Math.max(0, s.display_order + delta) });
      await load();
    } catch {
      setError('Eroare la actualizarea ordinii.');
    }
  }

  async function toggleActive(s: StaffSponsor) {
    try {
      await staffSponsorsService.update(s.id, { is_active: !s.is_active });
      await load();
    } catch {
      setError('Eroare la actualizare.');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await staffSponsorsService.delete(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch {
      setError('Eroare la ștergere.');
    }
  }

  const editing = isAdding || editingId !== null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>Parteneri & Sponsori</h1>
        {!editing && (
          <button
            onClick={startAdd}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--vivid-purple)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
          >
            <Plus className="h-4 w-4" />
            Adaugă
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {editing && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
            {isAdding ? `${KIND_LABEL[form.kind]} nou` : `Editare ${KIND_LABEL[form.kind].toLowerCase()}`}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Tip</label>
              <div className="flex gap-2">
                {(['partner', 'sponsor'] as SponsorKind[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k })}
                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                      form.kind === k
                        ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/10 text-[var(--vivid-purple)]'
                        : 'border-gray-200 text-muted-foreground hover:bg-gray-50'
                    }`}
                  >
                    {k === 'partner' ? <HeartHandshake className="h-4 w-4" /> : <Award className="h-4 w-4" />}
                    {KIND_LABEL[k]}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Partenerii sunt parteneri formali ai programului. Sponsorii susțin programul.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Nume</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Site web</label>
              <input
                type="url"
                value={form.website_url}
                onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                placeholder="https://exemplu.ro"
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Instagram</label>
              <input
                type="text"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                placeholder="@nume_cont sau link complet"
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Ordine afișare</label>
              <input
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
              />
              <p className="mt-1 text-xs text-muted-foreground">Număr mai mic apare primul.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Logo</label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <img src={logoPreview} alt="Preview" className="h-16 w-16 rounded-lg border object-cover" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--vivid-purple)]/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-[var(--vivid-purple)]"
                />
              </div>
            </div>
            <div className="sm:col-span-2 flex items-center">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--vivid-purple)]"
                />
                <span className="text-sm font-medium">Activ (vizibil public)</span>
              </label>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={cancelForm}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-[var(--vivid-purple)] px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
            >
              {saving ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          placeholder="Caută parteneri și sponsori..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-full border py-2.5 pl-10 pr-4 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Nume</th>
              <th className="px-4 py-3">Tip</th>
              <th className="hidden px-4 py-3 sm:table-cell">Site</th>
              <th className="px-4 py-3 w-32">Ordine</th>
              <th className="px-4 py-3">Stare</th>
              <th className="px-4 py-3 w-20 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {sponsors.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                  <Award className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
                  Niciun partener sau sponsor.
                </td>
              </tr>
            )}
            {sponsors.map((s) => (
              <tr key={s.id} className="border-t">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {s.logo ? (
                      <img src={mediaUrl(s.logo)} alt="" className="h-10 w-10 rounded-lg border object-cover" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--vivid-yellow-green)]/30">
                        {s.kind === 'partner' ? (
                          <HeartHandshake className="h-5 w-5 text-[var(--vivid-purple)]" />
                        ) : (
                          <Award className="h-5 w-5 text-[var(--vivid-purple)]" />
                        )}
                      </div>
                    )}
                    <div className="font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>{s.name}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    s.kind === 'partner'
                      ? 'bg-[var(--vivid-purple)]/10 text-[var(--vivid-purple)]'
                      : 'bg-[var(--vivid-yellow-green)]/30 text-foreground'
                  }`}>
                    {s.kind === 'partner' ? <HeartHandshake className="h-3 w-3" /> : <Award className="h-3 w-3" />}
                    {KIND_LABEL[s.kind]}
                  </span>
                </td>
                <td className="hidden px-4 py-3 sm:table-cell">
                  <div className="flex flex-col gap-0.5 text-xs">
                    {s.website_url ? (
                      <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-[var(--vivid-purple)] underline">
                        {s.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    ) : !s.instagram ? (
                      <span className="text-muted-foreground">—</span>
                    ) : null}
                    {s.instagram && (
                      <a
                        href={`https://instagram.com/${s.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[var(--vivid-purple)] underline"
                      >
                        <Instagram className="h-3 w-3" />
                        @{s.instagram}
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => nudgeOrder(s, -1)}
                      className="rounded p-1 hover:bg-gray-100"
                      title="Mai sus"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center text-sm font-mono">{s.display_order}</span>
                    <button
                      onClick={() => nudgeOrder(s, 1)}
                      className="rounded p-1 hover:bg-gray-100"
                      title="Mai jos"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleActive(s)}
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                      s.is_active
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {s.is_active ? 'Activ' : 'Inactiv'}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => startEdit(s)}
                      className="rounded p-1.5 hover:bg-gray-100"
                      title="Editează"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(s)}
                      className="rounded p-1.5 text-red-600 hover:bg-red-50"
                      title="Șterge"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={deleteTarget ? `Șterge ${KIND_LABEL[deleteTarget.kind].toLowerCase()}` : ''}
        description={deleteTarget ? `Sigur vrei să ștergi „${deleteTarget.name}"?` : ''}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
